import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ComparisonTable,
  MarketingPage,
  Section,
} from "@/components/marketing-page";
import { comparisonHead, type FAQ } from "@/lib/seo";

const PATH = "/vs/profound";
const TITLE = "Profound Alternative & Review: monroya.ai vs Profound";
const DESCRIPTION =
  "A fair review of Profound and how monroya.ai differs: named vs recommended vs cited, brand-name questions excluded, ranked moves with drafts, verified wins, and a free plan with paid plans from $79 a month.";
// Profound facts checked on tryprofound.com/pricing on this date.
const PROFOUND_CHECKED = "October 26, 2026";

const FAQS: FAQ[] = [
  {
    q: "Is Profound worth it for a small team?",
    a: "Usually not as a first tool. Profound's ongoing tracking sits on its Enterprise plan, which is priced through a sales demo, and its free trial lasts 7 days with a fixed prompt set you cannot edit. A small team that needs to see where it stands and what to fix this month gets there faster with a self-serve tool. Profound fits once AI visibility is a funded team program with dedicated headcount.",
  },
  {
    q: "What's a cheaper alternative to Profound?",
    a: "monroya.ai has a free plan with no card, and paid plans at $79 a month (50 questions checked weekly), $199 a month (150 questions) and $299 a month (400 questions, top 50 checked daily). Every plan is self-serve and listed on the pricing page, so there is no sales call to find out the price.",
  },
  {
    q: "Which AI assistants does each one check?",
    a: "Profound's trial covers ChatGPT, Gemini and Google AI Overviews, and its Enterprise plan tracks up to 9 answer engines. monroya.ai paid plans check ChatGPT, Claude, Gemini, Perplexity, Google AI Overviews and Google AI Mode.",
  },
  {
    q: "Which is better for a large enterprise?",
    a: "Profound. It offers tailored prompt plans, more answer engines, unlimited seats, dedicated support and an accounts team. monroya.ai is built for the operator who needs to move the number, not for an org-wide procurement process.",
  },
];

export const Route = createFileRoute("/vs/profound")({
  head: () =>
    comparisonHead({
      competitors: ["Profound"],
      path: PATH,
      breadcrumbLabel: "vs Profound",
      title: TITLE,
      description: DESCRIPTION,
      faqs: FAQS,
    }),
  component: VsProfound,
});

function VsProfound() {
  return (
    <MarketingPage
      checkCta
      stamp="Comparison"
      title="Profound alternative and review: monroya.ai vs Profound"
      dek="Profound is an enterprise AI visibility platform. monroya.ai is a self-serve alternative that tells you what to fix, drafts it, and checks that it worked."
      updated="October 2026"
      faqs={FAQS}
      related={[
        { label: "Otterly vs Profound vs Monroya", to: "/best-ai-visibility-tools" },
        { label: "Compare against AthenaHQ", to: "/vs/athenahq" },
        { label: "AI share-of-voice tools compared", to: "/compare/ai-visibility-share-of-voice-tools" },
        { label: "All alternatives in this category", to: "/vs" },
      ]}
    >
      <Section heading="Profound in one paragraph">
        <p>
          Profound runs structured prompts across AI platforms and reports where
          and how your brand appears, including citations, sentiment, ranking and
          competitive presence. It has two published plans. The free Trial runs 50
          prompts daily for 7 days across ChatGPT, Gemini and Google AI Overviews,
          using a recommended prompt set you cannot customize. Enterprise adds
          ongoing daily tracking, a tailored prompt plan, up to 9 answer engines,
          AI Marketer agent credits and dedicated support, priced through a demo.
          Both include unlimited seats, and a self-serve Agency Growth plan comes
          with 400 credits per month per client workspace. Checked on
          tryprofound.com/pricing, {PROFOUND_CHECKED}.
        </p>
      </Section>

      <Section heading="Where monroya.ai differs">
        <p>
          <strong className="text-ink">Named, recommended and cited are counted separately.</strong>{" "}
          Being mentioned in an answer is not the same as being recommended, and
          neither is the same as having your page cited as a source. monroya.ai
          reports all three, so a brand that is named but never picked does not
          look like a win.
        </p>
        <p>
          <strong className="text-ink">Brand-name questions are left out of the score.</strong>{" "}
          Asking "what is [your company]" almost always returns your company.
          monroya.ai scores the questions buyers ask before they know your name,
          like "best tool for X", so the number reflects real discovery.
        </p>
        <p>
          <strong className="text-ink">Ranked moves with drafts.</strong> Each gap
          becomes a job, ranked by what it is likely to change, with a draft ready
          to edit and publish.
        </p>
        <p>
          <strong className="text-ink">Wins are verified, then watched.</strong>{" "}
          A win counts only after repeated checks confirm it, and monroya.ai keeps
          tracking it and alerts you if it starts to slip.
        </p>
        <p>
          <strong className="text-ink">Free plan and $79 a month, not enterprise pricing.</strong>{" "}
          Start free with no card, then pick a plan on the{" "}
          <Link to="/pricing" className="font-medium text-ink underline underline-offset-4">
            pricing page
          </Link>
          . No demo needed to learn the price.
        </p>
      </Section>

      <Section heading="Side by side">
        <ComparisonTable
          sources={[{ label: "Profound pricing", url: "https://www.tryprofound.com/pricing" }]}
          checked="October 2026"
          headers={["", "Profound", "monroya.ai"]}
          rows={[
            ["Free option", "7-day trial, 50 fixed prompts", "Free plan, no card, no time limit"],
            ["Paid pricing", "Enterprise via demo; Agency Growth self-serve", "$79, $199 or $299 a month, listed publicly"],
            ["AI surfaces", "Trial: ChatGPT, Gemini, Google AI Overviews. Enterprise: up to 9", "ChatGPT, Claude, Gemini, Perplexity, Google AI Overviews, Google AI Mode"],
            ["Check frequency", "Daily", "Weekly; top 50 questions daily on $299 plan"],
            ["Edit your questions", "Enterprise only", "Every plan"],
            ["Named vs recommended vs cited", "Reports mentions, citations, sentiment, ranking", "Three separate counts"],
            ["Brand-name questions in score", "Not stated", "Excluded"],
            ["What to do next", "AI Marketer agents (credits)", "Ranked jobs with drafts"],
            ["Win checking", "Not stated", "Confirmed across repeated checks, tracked for slipping"],
            ["Seats and support", "Unlimited seats; dedicated support on Enterprise", "Self-serve"],
          ]}
        />
        <p className="mt-4 text-sm text-muted-foreground">
          Profound details from tryprofound.com/pricing, {PROFOUND_CHECKED}. Check
          their site for the latest.
        </p>
      </Section>

      <Section heading="Who should choose Profound">
        <p>
          A large brand or agency running AI visibility as a team program, that
          wants more answer engines, unlimited seats and an accounts team, and is
          comfortable buying through a demo.
        </p>
      </Section>

      <Section heading="Who should choose monroya.ai">
        <p>
          A small team or a single owner who needs to know whether AI recommends
          them, what to fix first, and proof that the fix worked, without a
          contract or a sales call.
        </p>
      </Section>
    </MarketingPage>
  );
}
